/**
 * @file index.tsx
 *
 * @fileoverview Creates a generic layout to be reused on pages, including Header, main content
 * and footer.
 */
import React from 'react';
import Header from '~/jobiqo/components/Header';
import dynamic from 'next/dynamic';
import { ErrorBoundary } from 'react-error-boundary';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Footer } from '~/jobiqo/components/Footer';

const LayoutMainError = dynamic(
  () => import('~/jobiqo/components/Layout/components/LayoutMainError'),
  {
    ssr: false,
  },
);
const Messages = dynamic(() => import('~/jobiqo/components/Messages'), {
  ssr: false,
});

interface AnonymousLayoutProps {
  /**
   * The content to display inside the layout container.
   */
  children: React.ReactNode;
  /**
   * True if the main content should be full width.
   */
  fullWidthContainer?: boolean;
  /**
   * Main content size.
   */
  mainContentSize?: 'small' | 'medium' | 'large' | 'xLarge';
  /**
   * Fixed header.
   *
   * @default false.
   *
   */
  fixedHeader?: boolean;
  /**
   * Hide the site header while keeping the footer visible.
   *
   * @default false.
   */
  hideHeader?: boolean;
}

export const AnonymousLayout = ({
  children,
  fullWidthContainer = false,
  mainContentSize = 'large',
  fixedHeader = false,
  hideHeader = false,
}: AnonymousLayoutProps) => {
  return (
    <>
      {!hideHeader && <Header fixed={fixedHeader} />}
      {fullWidthContainer ? (
        <>
          <main>
            <Messages fullWidth={true} />
            <ErrorBoundary fallback={<LayoutMainError />}>
              {children}
            </ErrorBoundary>
          </main>
          <>
            <Footer />
          </>
        </>
      ) : (
        <>
          <main>
            <Main size={mainContentSize}>
              <Spacing size="large" />
              <Messages size={mainContentSize} />
              <Spacing size="large" />
              <ErrorBoundary fallback={<LayoutMainError />}>
                {children}
              </ErrorBoundary>
            </Main>
          </main>
          <Spacing marginTop={[8, 12]} />
          <>
            <Footer />
          </>
        </>
      )}
    </>
  );
};

export default AnonymousLayout;
